import React, { useState } from "react";
import { sendEmail } from '../../services/EmailService';
import imageCompression from "browser-image-compression";
import "./WebsiteSignup.css";

const WEBSITE_PLAN = "Starter Website";

// Add website templates array
const WEBSITE_TEMPLATES = [
    {
        id: "base",
        name: "Base",
        description: "A solid foundation with essential features and a clean design.",
        img: require("../../assets/imgs/previews/agent/base.jpg")
    },
    {
        id: "luxury",
        name: "Luxury",
        description: "Elegant and sophisticated with high-quality visuals.",
        img: require("../../assets/imgs/previews/agent/luxury.jpg")
    },
    {
        id: "metropolitan",
        name: "Metropolitan",
        description: "Modern living with neutral tones and refined visuals.",
        img: require("../../assets/imgs/previews/agent/metropolitan.jpg")
    },
    {
        id: "skyline",
        name: "Skyline",
        description: "Condo-focused theme with high-rise and city views.",
        img: require("../../assets/imgs/previews/agent/skyline.jpg")
    },
    {
        id: "vibrance",
        name: "Vibrance",
        description: "Energetic, bold, and full of life.",
        img: require("../../assets/imgs/previews/agent/vibrance.jpg")
    },
    {
        id: "futuristic",
        name: "Futuristic",
        description: "High-tech aesthetics with vibrant neon and abstract visuals.",
        img: require("../../assets/imgs/previews/agent/futuristic.jpg")
    }
];

// Update buildEmailContent to include preferred template
function buildEmailContent(form) {
    const tableStyle = "border-collapse:collapse;width:100%;font-family:sans-serif;font-size:15px;";
    const thStyle = "background:#f5f5f5;text-align:left;padding:8px;border-bottom:1px solid #ddd;";
    const tdStyle = "padding:8px;border-bottom:1px solid #eee;";
    const row = (label, value) =>
        value ? `<tr><th style="${thStyle}">${label}</th><td style="${tdStyle}">${value}</td></tr>` : "";

    let html = `<h2 style="font-family:sans-serif;">Website Winner Information</h2>
    <table style="${tableStyle}">
        ${row("Name", form.name)}
        ${row("Email", form.email)}
        ${row("Phone", form.phone)}
        ${row("Company", form.company)}
        ${row("Bio/About Me", form.bio)}
        ${row("Quote", form.quote)}
    </table>`;

    // Testimonials
    if (form.testimonials && form.testimonials.length > 0 && form.testimonials.some(t => t.name || t.message)) {
        html += `<h3 style="font-family:sans-serif;">Testimonials</h3>
        <table style="${tableStyle}">`;
        form.testimonials.forEach((t, idx) => {
            if (t.name || t.message) {
                html += `
                <tr>
                    <th style="${thStyle}">Name</th>
                    <td style="${tdStyle}">${t.name || ""}</td>
                </tr>
                <tr>
                    <th style="${thStyle}">Message</th>
                    <td style="${tdStyle}">${t.message || ""}</td>
                </tr>
                `;
            }
        });
        html += `</table>`;
    }

    // Company Info
    if (form.companyInfo && (
        form.companyInfo.name || form.companyInfo.phone || form.companyInfo.email || form.companyInfo.address
    )) {
        html += `<h3 style="font-family:sans-serif;">Company Information</h3>
        <table style="${tableStyle}">
            ${row("Company Name", form.companyInfo.name)}
            ${row("Company Phone", form.companyInfo.phone)}
            ${row("Company Email", form.companyInfo.email)}
            ${row("Company Address", form.companyInfo.address)}
        </table>`;
    }

    // Website Info
    html += `<h3 style="font-family:sans-serif;">Website Information</h3>
    <table style="${tableStyle}">
        ${row("Website Quote", form.websiteQuote)}
        ${row("Domain", form.domain)}
    </table>`;

    // Social Media Links
    html += `<h3 style="font-family:sans-serif;">Social Media Links</h3>
    <table style="${tableStyle}">
        ${row("Twitter/X", form.socialLinks.twitter)}
        ${row("Facebook", form.socialLinks.facebook)}
        ${row("Instagram", form.socialLinks.instagram)}
        ${row("YouTube", form.socialLinks.youtube)}
        ${row("LinkedIn", form.socialLinks.linkedin)}
        ${row("TikTok", form.socialLinks.tiktok)}
    </table>`;

    // Preferred Template
    if (form.preferredTemplate) {
        const templateObj = WEBSITE_TEMPLATES.find(t => t.id === form.preferredTemplate);
        html += `<h3 style="font-family:sans-serif;">Preferred Website Template</h3>
        <table style="${tableStyle}">
            <tr>
                <th style="${thStyle}">Template</th>
                <td style="${tdStyle}">${templateObj ? templateObj.name : form.preferredTemplate}</td>
            </tr>
            <tr>
                <th style="${thStyle}">Description</th>
                <td style="${tdStyle}">${templateObj ? templateObj.description : ""}</td>
            </tr>
        </table>`;
    }

    return html;
}

const initialTestimonial = { name: "", picture: null, message: "" };

const WebsiteSignup = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        picture: null,
        company: "",
        plan: WEBSITE_PLAN,
        bio: "",
        quote: "",
        testimonials: [ { ...initialTestimonial } ],
        companyInfo: {
            name: "",
            phone: "",
            email: "",
            logo: null,
            address: ""
        },
        websiteMainPicture: null,
        websiteQuote: "",
        domain: "",
        socialLinks: {
            twitter: "",
            facebook: "",
            instagram: "",
            youtube: "",
            linkedin: "",
            tiktok: ""
        },
        preferredTemplate: "",
    });
    const [errors, setErrors] = useState({});

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (name.startsWith("companyInfo.")) {
            const key = name.split(".")[1];
            setForm(f => ({
                ...f,
                companyInfo: { ...f.companyInfo, [key]: type === "file" ? files[0] : value }
            }));
        } else if (name.startsWith("socialLinks.")) {
            const key = name.split(".")[1];
            setForm(f => ({
                ...f,
                socialLinks: { ...f.socialLinks, [key]: value }
            }));
        } else if (name === "picture" || name === "websiteMainPicture") {
            setForm(f => ({ ...f, [name]: files[0] }));
        } else if (name === "preferredTemplate") {
            setForm(f => ({ ...f, preferredTemplate: value }));
        } else {
            setForm(f => ({ ...f, [name]: value }));
        }
    };

    // Testimonials handlers
    const handleTestimonialChange = (idx, field, value, files) => {
        setForm(f => {
            const testimonials = f.testimonials.map((t, i) =>
                i === idx ? { ...t, [field]: field === "picture" ? files[0] : value } : t
            );
            return { ...f, testimonials };
        });
    };
    const addTestimonial = () => {
        setForm(f => ({ ...f, testimonials: [ ...f.testimonials, { ...initialTestimonial } ] }));
    };
    const removeTestimonial = (idx) => {
        setForm(f => ({ ...f, testimonials: f.testimonials.filter((_, i) => i !== idx) }));
    };

    // Validation (simple example)
    const validate = () => {
        const errs = {};
        if (!form.name) errs.name = "Name required";
        if (!form.email) errs.email = "Email required";
        if (!form.phone) errs.phone = "Phone required";
        if (!form.companyInfo.name) errs.companyInfoName = "Company Name required";
        // ...add more as needed
        return errs;
    };

    // Helper to collect all attachments
    function collectAttachments(form) {
        const files = [];
        if (form.picture) files.push(form.picture);
        if (form.testimonials && form.testimonials.length > 0) {
            form.testimonials.forEach(t => {
                if (t.picture) files.push(t.picture);
            });
        }
        if (form.companyInfo && form.companyInfo.logo) files.push(form.companyInfo.logo);
        if (form.websiteMainPicture) files.push(form.websiteMainPicture);
        return files;
    }

    // Reset form helper
    const reset = () => {
        setForm({
            name: "",
            email: "",
            phone: "",
            picture: null,
            company: "",
            plan: WEBSITE_PLAN,
            bio: "",
            quote: "",
            testimonials: [ { ...initialTestimonial } ],
            companyInfo: {
                name: "",
                phone: "",
                email: "",
                logo: null,
                address: ""
            },
            websiteMainPicture: null,
            websiteQuote: "",
            domain: "",
            socialLinks: {
                twitter: "",
                facebook: "",
                instagram: "",
                youtube: "",
                linkedin: "",
                tiktok: ""
            },
            preferredTemplate: "",
        });
    };

    // Submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setIsSubmitting(true);
        try {
            const content = buildEmailContent(form);
            let attachments = collectAttachments(form);

            // Calculate total size in bytes
            let totalSize = attachments.reduce((sum, file) => sum + (file.size || 0), 0);

            // 10MB = 10 * 1024 * 1024 bytes
            if (totalSize > 10 * 1024 * 1024) {
                // compress images
                attachments = await Promise.all(attachments.map(async file => {
                    if (file.type && file.type.startsWith('image/')) {
                        return await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920 });
                    }
                    return file;
                }));
                totalSize = attachments.reduce((sum, file) => sum + (file.size || 0), 0);
                if (totalSize > 10 * 1024 * 1024) {
                    setError('Total attachment size exceeds 10MB. Please remove some files or use smaller images.');
                    return;
                }
            }

            await sendEmail({
                recipientEmail: [process.env.REACT_APP_RECIPIENT_EMAIL, process.env.REACT_APP_RECIPIENT_EMAIL2, process.env.REACT_APP_RECIPIENT_EMAIL3, process.env.REACT_APP_RECIPIENT_EMAIL4],
                senderEmail: process.env.REACT_APP_SENDER_EMAIL,
                subject: `APTO Websites Website Winner`,
                content,
                senderName: form.name,
                replyTo: form.email,
                attachments
            });
            setSuccess('Your information has been sent! We will contact you soon.');
            reset();
        } catch (error) {
            setError('There was an error sending your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="signup-outer-wrapper">
            <div className="signup-container winner-form-pushed">
                <h1>Website Information</h1>
                <div className="selected-plan-section">
                </div>
                <form className="signup-form" onSubmit={handleSubmit}>
                    {/* General Info */}
                    <section>
                        <h2>General Information</h2>
                        <div className="form-grid">
                            <div className="form-row">
                                <label>
                                    Name
                                    <span className={form.name ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.name && !form.name ? " required-error" : ""}`}
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Your full name"
                                />
                                {errors.name && !form.name && <span className="error">{errors.name}</span>}
                            </div>
                            <div className="form-row">
                                <label>
                                    Email
                                    <span className={form.email ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.email && !form.email ? " required-error" : ""}`}
                                    name="email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@email.com"
                                />
                                {errors.email && !form.email && <span className="error">{errors.email}</span>}
                            </div>
                            <div className="form-row">
                                <label>
                                    Phone
                                    <span className={form.phone ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.phone && !form.phone ? " required-error" : ""}`}
                                    name="phone"
                                    value={form.phone}
                                    onChange={handleChange}
                                    placeholder="(555) 555-5555"
                                />
                                {errors.phone && !form.phone && <span className="error">{errors.phone}</span>}
                            </div>
                            <div className="form-row">
                                <label>Picture/Headshot</label>
                                <input className="styled-input" name="picture" type="file" accept="image/*" onChange={handleChange} />
                            </div>
                        </div>
                    </section>

                    {/* Bio/About Me */}
                    <section>
                        <h2>Bio / About Me</h2>
                        <div className="form-row">
                            <label>Bio/About Me</label>
                            <textarea className="styled-input" name="bio" value={form.bio} onChange={handleChange} placeholder="Tell us about yourself" />
                        </div>
                        <div className="form-row">
                            <label>Quote</label>
                            <input className="styled-input" name="quote" value={form.quote} onChange={handleChange} placeholder="Favorite quote" />
                        </div>
                    </section>

                    {/* Testimonials */}
                    <section>
                        <h2>Testimonials <button type="button" onClick={addTestimonial}>Add</button></h2>
                        {form.testimonials.map((t, idx) => (
                            <div className="testimonial-row" key={idx}>
                                <div className="form-grid">
                                    <div className="form-row">
                                        <label>Name</label>
                                        <input className="styled-input" value={t.name} onChange={e => handleTestimonialChange(idx, "name", e.target.value)} placeholder="Testimonial name" />
                                    </div>
                                    <div className="form-row">
                                        <label>Picture</label>
                                        <input className="styled-input" type="file" accept="image/*" onChange={e => handleTestimonialChange(idx, "picture", null, e.target.files)} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <label>Message</label>
                                    <textarea className="styled-input" value={t.message} onChange={e => handleTestimonialChange(idx, "message", e.target.value)} placeholder="Testimonial message" />
                                </div>
                                <button type="button" onClick={() => removeTestimonial(idx)} className="remove-btn">Remove</button>
                            </div>
                        ))}
                    </section>

                    {/* Company Info */}
                    <section>
                        <h2>Company Information</h2>
                        <div className="form-grid">
                            <div className="form-row">
                                <label>
                                    Name
                                    <span className={form.companyInfo.name ? "required-star-filled" : "required-star"}>*</span>
                                </label>
                                <input
                                    className={`styled-input${errors.companyInfoName && !form.companyInfo.name ? " required-error" : ""}`}
                                    name="companyInfo.name"
                                    value={form.companyInfo.name}
                                    onChange={handleChange}
                                    placeholder="Company name"
                                />
                                {errors.companyInfoName && !form.companyInfo.name && <span className="error">{errors.companyInfoName}</span>}
                            </div>
                            <div className="form-row">
                                <label>Phone</label>
                                <input className="styled-input" name="companyInfo.phone" value={form.companyInfo.phone} onChange={handleChange} placeholder="(555) 555-5555" />
                            </div>
                            <div className="form-row">
                                <label>Email</label>
                                <input className="styled-input" name="companyInfo.email" value={form.companyInfo.email} onChange={handleChange} placeholder="company@email.com" />
                            </div>
                            <div className="form-row">
                                <label>Logo</label>
                                <input className="styled-input" name="companyInfo.logo" type="file" accept="image/*" onChange={handleChange} />
                            </div>
                            <div className="form-row" style={{gridColumn: "1 / -1"}}>
                                <label>Address</label>
                                <input className="styled-input" name="companyInfo.address" value={form.companyInfo.address} onChange={handleChange} placeholder="Company address" />
                            </div>
                        </div>
                    </section>

                    {/* Website Information */}
                    <section>
                        <h2>Website Information</h2>
                        <div className="form-grid">
                            <div className="form-row">
                                <label>Main Picture</label>
                                <input className="styled-input" name="websiteMainPicture" type="file" accept="image/*" onChange={handleChange} />
                            </div>
                            <div className="form-row">
                                <label>Quote</label>
                                <input className="styled-input" name="websiteQuote" value={form.websiteQuote} onChange={handleChange} placeholder="Website quote" />
                            </div>
                            <div className="form-row" style={{gridColumn: "1 / -1"}}>
                                <label>Domain Name</label>
                                <input className="styled-input" name="domain" value={form.domain} onChange={handleChange} placeholder="yourdomain.com" />
                            </div>
                        </div>
                    </section>

                    {/* Social Media Links */}
                    <section>
                        <h2>Social Media Links</h2>
                        <div className="form-grid">
                            {["twitter", "facebook", "instagram", "youtube", "linkedin", "tiktok"].map(platform => {
                                const platformDisplay = {
                                    twitter: "Twitter/X",
                                    facebook: "Facebook",
                                    instagram: "Instagram",
                                    youtube: "YouTube",
                                    linkedin: "LinkedIn",
                                    tiktok: "TikTok"
                                };
                                return (
                                    <div className="form-row" key={platform}>
                                        <label>{platformDisplay[platform]} Link</label>
                                        <input className="styled-input" name={`socialLinks.${platform}`} value={form.socialLinks[platform]} onChange={handleChange} placeholder={platformDisplay[platform]} />
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Website Template Selection Section */}
                    <section>
                        <h2>Choose Your Preferred Website Template</h2>
                        <div style={{display: "flex", flexWrap: "wrap", gap: "18px", justifyContent: "center"}}>
                            {WEBSITE_TEMPLATES.map(template => (
                                <label
                                    key={template.id}
                                    style={{
                                        border: form.preferredTemplate === template.id ? "2px solid #1a4d8f" : "1.5px solid #e3eafc",
                                        borderRadius: "12px",
                                        boxShadow: form.preferredTemplate === template.id ? "0 4px 16px rgba(13,71,161,0.10)" : "0 2px 8px rgba(13,71,161,0.05)",
                                        padding: "12px",
                                        cursor: "pointer",
                                        background: "#fff",
                                        maxWidth: 220,
                                        minWidth: 180,
                                        flex: "1 1 180px",
                                        textAlign: "center",
                                        transition: "box-shadow 0.18s, border 0.18s"
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="preferredTemplate"
                                        value={template.id}
                                        checked={form.preferredTemplate === template.id}
                                        onChange={handleChange}
                                        style={{marginBottom: 8}}
                                    />
                                    <div style={{marginBottom: 8}}>
                                        <img
                                            src={template.img}
                                            alt={template.name}
                                            style={{
                                                width: "100%",
                                                maxWidth: 160,
                                                borderRadius: 8,
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                                marginBottom: 6
                                            }}
                                        />
                                    </div>
                                    <div style={{fontWeight: 700, color: "#1a4d8f", fontSize: "1.05em"}}>{template.name}</div>
                                </label>
                            ))}
                        </div>
                        {!form.preferredTemplate && (
                            <div style={{color: "#d32f2f", marginTop: 8, textAlign: "center", fontSize: "0.98em"}}>
                                Please select a template.
                            </div>
                        )}
                    </section>

                    {error && <div className="error-message" role="alert">{error}</div>}
                    {/* Success message only at the bottom */}
                    {success && (
                        <div className="success-message" role="status">
                            Your signup request has been sent! We will contact you soon.
                        </div>
                    )}
                    <button type="submit" className="submit-btn" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default WebsiteSignup;
